import { type LineIndex, type NearestResult } from "@/utils/lineSampling";

const FIRST_PASS_MATCH_RADIUS_M = 80;
const LOOP_MATCH_RADIUS_M = 20;
const LOOP_MATCH_DISTANCE_TOLERANCE_M = 8;
const LOOP_MIN_ROUTE_SEPARATION_M = 120;
const LOOP_MIN_ROUTE_SEPARATION_KM = LOOP_MIN_ROUTE_SEPARATION_M / 1000;

export function firstProgress(progress: number[] | undefined, fallback = 1): number {
	return progress?.[0] ?? fallback;
}

export function progressValues(progress: number[] | undefined, fallback?: number): number[] {
	if (progress && progress.length > 0) return progress;
	return fallback === undefined ? [] : [fallback];
}

function dedupeLoopMatches(matches: NearestResult[]): NearestResult[] {
	if (matches.length <= 1) return matches;

	const sorted = [...matches].sort((a, b) => a.location - b.location);
	const clusters: NearestResult[] = [];
	let clusterLast = sorted[0].location;
	let clusterBest = sorted[0];

	for (let i = 1; i < sorted.length; i++) {
		const match = sorted[i];
		if (match.location - clusterLast < LOOP_MIN_ROUTE_SEPARATION_KM) {
			if (match.distanceMeters < clusterBest.distanceMeters) clusterBest = match;
			clusterLast = match.location;
			continue;
		}

		clusters.push(clusterBest);
		clusterLast = match.location;
		clusterBest = match;
	}
	clusters.push(clusterBest);

	return clusters;
}

function getMarkerProgressMatches(nearbyMatches: NearestResult[]): NearestResult[] {
	const strictMatches = nearbyMatches.filter(match => match.distanceMeters <= LOOP_MATCH_RADIUS_M);
	if (strictMatches.length > 0) {
		const loopMatches = dedupeLoopMatches(strictMatches);
		const closestDistance = Math.min(...loopMatches.map(match => match.distanceMeters));
		return loopMatches.filter(match => Math.abs(match.distanceMeters - closestDistance) <= LOOP_MATCH_DISTANCE_TOLERANCE_M);
	}

	const firstPass = dedupeLoopMatches(nearbyMatches)[0];
	return firstPass ? [firstPass] : [];
}

export function getMarkerProgresses(index: LineIndex, coords: [number, number], trackLength: number): number[] {
	if (trackLength <= 0) return [0];

	let matches: Array<{ location: number }> = getMarkerProgressMatches(index.nearby(coords, FIRST_PASS_MATCH_RADIUS_M));

	if (matches.length === 0) {
		const pt = index.nearest(coords);
		matches = pt ? [pt] : [];
	}

	return matches.length > 0
		? matches.map(match => match.location / trackLength)
		: [0];
}
